/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Loader2, Calendar, CalendarDays } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import AppLayout from '../../../layouts/AppLayout';
import EventCalendar from './components/EventCalendar';
import EventDetailModal from './components/EventDetailModal';
import { fetchDevEvents } from './utils/eventsParser';

function Events({ title }) {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const devEvents = await fetchDevEvents();

      // Sort events by date
      const sortedEvents = devEvents.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      setEvents(sortedEvents);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handlePrevMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  // Get upcoming events (next 30 days)
  const upcomingEvents = events
    .filter((event) => {
      const eventStart = new Date(event.startDate);
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      return eventStart >= today && eventStart <= thirtyDaysFromNow;
    })
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 5);

  if (isLoading) {
    return (
      <AppLayout title={title}>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={title}>
      <div className="h-full overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Calendar - Main Area */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-primary" />
                    Events Calendar
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <EventCalendar
                    currentDate={currentDate}
                    events={events}
                    onPrevMonth={handlePrevMonth}
                    onNextMonth={handleNextMonth}
                    onEventClick={handleEventClick}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Events Sidebar */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Upcoming
                    </CardTitle>
                    <Badge variant="secondary">{upcomingEvents.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {upcomingEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Calendar className="h-10 w-10 text-muted-foreground/50 mb-3" />
                      <p className="text-sm text-muted-foreground">
                        No upcoming events in the next 30 days
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {upcomingEvents.map((event) => {
                        const eventDate = new Date(event.startDate);
                        const day = eventDate.getDate();
                        const month = eventDate.toLocaleString('en-US', { month: 'short' });

                        return (
                          <button
                            key={event._id}
                            type="button"
                            className="w-full flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                            onClick={() => handleEventClick(event)}
                          >
                            <div className="flex flex-col items-center justify-center min-w-[40px] h-12 rounded-lg bg-primary/10 text-primary">
                              <span className="text-xs font-medium uppercase">{month}</span>
                              <span className="text-lg font-bold leading-none">{day}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{event.title}</p>
                              {event.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                  {event.description}
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="space-y-1">
                      <p className="text-2xl font-bold text-primary">{events.length}</p>
                      <p className="text-xs text-muted-foreground">Total Events</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-2xl font-bold text-primary">{upcomingEvents.length}</p>
                      <p className="text-xs text-muted-foreground">Upcoming</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      <EventDetailModal event={selectedEvent} isOpen={isModalOpen} onClose={handleCloseModal} />
    </AppLayout>
  );
}

Events.propTypes = {
  title: PropTypes.string.isRequired,
};

export default Events;
